import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { usePurchase } from "@/context/PurchaseContext";
import { HeartButton } from "@/components/HeartButton";
import { API } from "@/lib/api";
import { Play, Pause, Flame, ShoppingBag, Check, Loader2, Headphones } from "lucide-react";

const TrendingCard = ({ track, rank, list, owned }) => {
  const { current, isPlaying, playTrack } = usePlayer();
  const { openBuy, loadingId } = usePurchase();
  const isCurrent = current?.id === track.id;
  const loading = loadingId === track.id;

  return (
    <div data-testid={`trending-card-${track.id}`}
      className="group relative shrink-0 w-[220px] bg-[#121212] border border-[#2a2a2a] rounded-xl overflow-hidden hover:border-[#e2ff31]/50 transition-colors">
      <div className="relative aspect-square">
        <img src={`${API}${track.cover_url}`} alt={track.title} className="w-full h-full object-cover" />
        <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/70 backdrop-blur text-[#e2ff31] flex items-center justify-center font-heading font-bold text-sm">
          {rank}
        </span>
        {!track.sold && (
          <div className="absolute top-1 right-1 bg-black/50 backdrop-blur rounded-full">
            <HeartButton track={track} />
          </div>
        )}
        <button data-testid={`trending-play-${track.id}`} onClick={() => playTrack(track, list)}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ opacity: isCurrent ? 1 : undefined }}>
          <span className="w-12 h-12 rounded-full bg-[#e2ff31] text-black flex items-center justify-center">
            {isCurrent && isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
          </span>
        </button>
      </div>
      <div className="p-3">
        <p className={`font-medium truncate ${isCurrent ? "text-[#e2ff31]" : ""}`}>{track.title}</p>
        <div className="flex items-center justify-between mt-0.5 mb-3">
          <span className="text-xs text-[#9ca3af] truncate">{track.producer}</span>
          <span className="flex items-center gap-1 text-xs font-mono text-[#6b7280] shrink-0"><Headphones className="w-3 h-3" /> {(track.play_count || 0).toLocaleString()}</span>
        </div>
        {owned ? (
          <span className="flex items-center justify-center gap-1 text-sm text-[#22c55e] font-medium py-2"><Check className="w-4 h-4" /> Owned</span>
        ) : track.sold ? (
          <span data-testid={`trending-sold-${track.id}`} className="block text-center text-sm font-bold text-[#ef4444] border border-[#ef4444]/40 rounded-full py-2 uppercase tracking-wide">Sold</span>
        ) : (
          <button data-testid={`trending-buy-${track.id}`} onClick={() => openBuy(track)} disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold text-sm py-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} ${track.price?.toFixed(2)}
          </button>
        )}
      </div>
    </div>
  );
};

export const TrendingBeats = ({ tracks, owned }) => {
  const trending = [...tracks]
    .filter((t) => (t.play_count || 0) > 0)
    .sort((a, b) => (b.play_count || 0) - (a.play_count || 0))
    .slice(0, 8);

  if (trending.length === 0) return null;

  return (
    <section className="px-6 md:px-12 pt-12 relative z-10" data-testid="trending-section">
      <div className="flex items-center gap-2 mb-5">
        <Flame className="w-6 h-6 text-[#e2ff31]" />
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">Trending now</h2>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1" style={{ scrollbarWidth: "thin" }}>
        {trending.map((t, i) => (
          <TrendingCard key={t.id} track={t} rank={i + 1} list={trending} owned={owned.has(t.id)} />
        ))}
      </div>
    </section>
  );
};
